import { useEffect } from 'react';
import { useStore } from 'react-redux';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import type { RootState } from 'features/app/store/store';
import {
  VoiceMeetingEventBus,
  VoiceMeetingService,
  VoiceSessionCleanupIntent,
} from 'features/voice-meeting/services';
import {
  setCallDirection,
  callDirectionSelector,
} from 'features/call/call-hearing/store';

/**
 * Hook that tracks whether a Chime hearing call is inbound or outbound
 * Agent Places Hearing Call -> Outbound
 * Agent Receives Hearing Call -> Inbound
 * These are not related exclusively to H2D or D2H calls as H2D can be inbound or outbound
 * D2H can be outbound only
 * @returns Object containing the current call direction
 */
export const useVoiceCallDirection = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  // Get the current direction from Redux state instead of local state
  const currentDirection = useAppSelector(callDirectionSelector);
  const voiceMeetingService = VoiceMeetingService.getInstance();

  useEffect(() => {
    // Set initial value from existing session if available
    const initialDirection = voiceMeetingService.getCallDirection();

    // Only dispatch if there's a direction and it's different from current state
    if (initialDirection && initialDirection !== currentDirection) {
      dispatch(setCallDirection(initialDirection));
    }

    // Also listen for cleanup intents to ensure direction is reset to outbound
    // when the local voice session is torn down for any reason.
    const resetSubscription =
      VoiceMeetingEventBus.voiceSessionCleanup$.subscribe((intent) => {
        if (
          ![
            VoiceSessionCleanupIntent.FinishAndDeleteVoiceSession,
            VoiceSessionCleanupIntent.ResetLocalSession,
            VoiceSessionCleanupIntent.DetachLocalSession,
          ].includes(intent)
        ) {
          return;
        }

        // Read the live direction, not the render-time closure value. Several
        // cleanup intents can fire before React re-renders, and a stale value
        // would dispatch the same outbound update once per intent.
        if (callDirectionSelector(store.getState()) !== 'outbound') {
          dispatch(setCallDirection('outbound'));
        }
      });

    return () => {
      resetSubscription.unsubscribe();
    };
  }, [dispatch, store, voiceMeetingService, currentDirection]);

  return {
    callDirection: currentDirection,
    isInbound: currentDirection === 'inbound',
    isOutbound: currentDirection === 'outbound',
  };
};
